import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { KeyResult } from '@/types/objectives';

import { useUpdateKeyResultProgress } from '@/api/objectives';
import { ObjectiveQueryKey } from '@/constants/QueryKeys';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { clampToKeyResultRange } from '@/utils/objectiveProgress';

interface UseKeyResultProgressCommitParams {
    objectiveId: string;
    keyResult: KeyResult;
}

/**
 * A burst of steps -- five quick clicks on the stepper's chevrons -- still writes once per step,
 * the server contract is untouched; but the reader needs one confirmation, not five stacked
 * toasts. The toast is therefore posted this long after the *last* successful write, every write
 * inside the window pushing it back. 600ms sits well above a rapid click cadence (~200ms) and
 * above the bar's own 400ms arrow-key grouping, so a whole burst collapses into one message; on a
 * single change the delay reads as the round trip it follows, not as a wait.
 */
const SAVED_TOAST_GROUPING_DELAY_MS = 600;

/**
 * The single place a key result's `currentValue` gets persisted: clamps to range, calls the
 * mutation, replaces the cached objective, reports failures and confirms a run of successful
 * writes with a single grouped toast. Shared by
 * `KeyResultProgressControl` (the numeric field / Yes-No toggle) and `KeyResultProgressBar`
 * (the clickable bar, wired in from the detail page) so the two never drift into two slightly
 * different commit flows for the same server call.
 */
export const useKeyResultProgressCommit = ({
    objectiveId,
    keyResult,
}: UseKeyResultProgressCommitParams) => {
    const { t } = useTranslation();
    const { handleError } = useError();
    const queryClient = useQueryClient();
    const mutation = useUpdateKeyResultProgress();
    const savedToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Drops a still-pending confirmation on unmount: leaving the page mid-burst must not pop a
    // toast about a measurement the reader can no longer see.
    useEffect(() => () => clearTimeout(savedToastTimeoutRef.current), []);

    const scheduleSavedToast = () => {
        clearTimeout(savedToastTimeoutRef.current);
        savedToastTimeoutRef.current = setTimeout(() => {
            showSuccessToast({ message: t('objectives.detail.progressSaved') });
        }, SAVED_TOAST_GROUPING_DELAY_MS);
    };

    /** Resolves to the value the caller should now display: the clamped value on success (or a
     * no-op), the previous server value if the mutation failed. */
    const commit = async (nextValue: number): Promise<number> => {
        // The server stores the value as sent, so nothing outside [start, target] leaves here.
        const clamped = clampToKeyResultRange({
            startValue: keyResult.startValue,
            targetValue: keyResult.targetValue,
            value: nextValue,
        });

        if (clamped === keyResult.currentValue) {
            return clamped;
        }

        try {
            const updatedObjective = await mutation.mutateAsync({
                objectiveId,
                keyResultId: keyResult.id,
                request: { currentValue: clamped },
            });
            // Replace the cached objective immediately; the hook's own `onSuccess` also
            // invalidates it right after, so a background refetch confirms it.
            queryClient.setQueryData([ObjectiveQueryKey.OBJECTIVE, objectiveId], updatedObjective);
            scheduleSavedToast();
            return clamped;
        } catch (error) {
            handleError({ error });
            return keyResult.currentValue;
        }
    };

    return { commit, isPending: mutation.isPending };
};
